import { useCallback, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'

import { Icon } from '@/components/icon'
import { Button } from '@/components/ui/button'
import { DownloadManager } from '@/features/empreendimentos/shared/domain/download-manager.entity'
import { useArquivosDownloadMultiplos } from '@/features/empreendimentos/shared/hooks/use-arquivos-download-multiplos'
import { useEmpreendimentoMidias } from '@/features/empreendimentos/shared/hooks/use-empreendimento-midias'

import { PainelAcoes } from './painel-acoes'
import { ZoomableImage } from './zoomable-image'

export type MidiaTipo = 'fotos' | 'plantas'

interface EmpreendimentoMidiasParams {
  config: {
    titulo: string
    descricao: string
  }
}

export const EmpreendimentoMidias = ({ config }: EmpreendimentoMidiasParams) => {
  const { idExterno, midiaSelecionada } = useParams<{ idExterno: string; midiaSelecionada: MidiaTipo }>()
  const { t } = useTranslation('empreendimentos')

  const [isSelectionMode, setIsSelectionMode] = useState(false)
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set())

  const { data: midias } = useEmpreendimentoMidias(Number(idExterno), midiaSelecionada!)
  const downloadMultiplos = useArquivosDownloadMultiplos()

  const buttonText = useMemo(() => {
    if (downloadMultiplos.isPending) return t('pages.midias.acoes.baixando')
    return isSelectionMode ? t('pages.midias.acoes.cancelar') : t('pages.midias.acoes.selecionar')
  }, [downloadMultiplos.isPending, isSelectionMode, t])

  const handleToggleSelectionMode = useCallback(() => {
    if (isSelectionMode) setSelectedImages(new Set())
    setIsSelectionMode(!isSelectionMode)
  }, [isSelectionMode])

  const handleImageClick = useCallback(
    (index: number) => {
      if (!isSelectionMode) return

      setSelectedImages((prev) => {
        const newSet = new Set(prev)
        if (newSet.has(index)) {
          newSet.delete(index)
        } else {
          newSet.add(index)
        }
        return newSet
      })
    },
    [isSelectionMode],
  )

  const handleDownload = useCallback(() => {
    const imagesToDownload = midias.filter((_, index) => selectedImages.has(index))
    const imageUrls = imagesToDownload.map((midia) => midia.uri)

    const onSuccess = () => {
      setIsSelectionMode(false)
      setSelectedImages(new Set())
    }

    if (selectedImages.size === 1 && imagesToDownload?.[0]) {
      DownloadManager.downloadWithLink(imagesToDownload[0].uri, imagesToDownload[0].nome).then(onSuccess)
    } else {
      downloadMultiplos.mutate(imageUrls, { onSuccess })
    }
  }, [downloadMultiplos, midias, selectedImages])

  return (
    <div className="grid gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="mb-2 text-base leading-none font-normal tracking-widest uppercase">{config.titulo}</h1>
          <p className="text-sm leading-none font-normal tracking-wider text-neutral-500">{config.descricao}</p>
        </div>

        <Button
          onClick={handleToggleSelectionMode}
          variant={isSelectionMode ? 'outline' : 'default'}
          disabled={downloadMultiplos.isPending}
        >
          {downloadMultiplos.isPending && <Icon name="loader" size={16} className="animate-spin" />}
          {buttonText}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {midias.map((midia, index) => (
          <ZoomableImage
            key={`${midia.nome}-${index}`}
            src={midia.uri}
            alt={midia.nome}
            images={midias}
            initialIndex={index}
            isSelectionMode={isSelectionMode}
            isSelected={selectedImages.has(index)}
            onSelect={() => handleImageClick(index)}
          />
        ))}
      </div>

      {isSelectionMode && selectedImages.size > 0 && (
        <PainelAcoes
          quantidadeSelecionada={selectedImages.size}
          isLoading={downloadMultiplos.isPending}
          onDownload={handleDownload}
          onCancelar={handleToggleSelectionMode}
          midiaTipo={midiaSelecionada === 'fotos' ? 'foto' : 'planta'}
        />
      )}
    </div>
  )
}
